import { FC, useCallback, useMemo } from "react";
import { Button, Dialog, Stack } from "@chakra-ui/react";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";

import { CASE_FILE_KIND, CaseDocumentFileModel } from "@shared/models";
import { useLoader } from "@shared/hooks/LoaderHook";
import { CASE_DOCUMENT_LIMITS } from "@containers/Cases/constants";
import { useCaseDocumentUpload } from "@containers/Cases/hooks";
import { parseCaseDocument } from "@containers/Cases/store/actions";

import {
  CaseDocumentDropZone,
  CaseDocumentFileRow,
  CaseDocumentModalHeader,
  CaseDocumentRemoveFileButton,
  CaseDocumentUploadRow,
} from "../CaseDocumentModal";
import { formatMegaBytes } from "../CaseDocuments.utils";

const LIMITS = CASE_DOCUMENT_LIMITS[CASE_FILE_KIND.NOTES];

interface CaseNotesUploadStepProps {
  caseId: number;
  documentFiles: CaseDocumentFileModel[];
  /** Lists the document's file so that it can be removed */
  replace: boolean;
  subtitle: string;
  onClose: () => void;
  onParse: () => void;
}

const CaseNotesUploadStep: FC<CaseNotesUploadStepProps> = ({
  caseId,
  documentFiles,
  replace,
  subtitle,
  onClose,
  onParse,
}) => {
  const dispatch = useDispatch();
  const { rows, readyIds, existingFiles, replacedIds, canParse, addFiles, replaceRow, removeRow, removeExistingFile } =
    useCaseDocumentUpload(caseId, CASE_FILE_KIND.NOTES, documentFiles, replace);
  const row = rows.at(0);
  const listedFiles = replace ? existingFiles : [];

  const { isLoading } = useLoader({
    name: "parseCaseNotes",
    actionTypes: useMemo(() => [parseCaseDocument], []),
  });

  // Case notes take one file, so only the first of several dropped files is kept.
  const handleFile = useCallback(
    (file: File | File[]) => addFiles(file instanceof File ? [file] : Array.from(file).slice(0, 1)),
    [addFiles],
  );

  const handleParse = () => {
    dispatch(
      parseCaseDocument.request({
        caseId,
        kind: CASE_FILE_KIND.NOTES,
        fileIds: readyIds,
        replacedFileIds: replacedIds,
        cb: () => onParse(),
      }),
    );
  };

  return (
    <>
      <CaseDocumentModalHeader title={replace ? "Replace Case Notes" : "Upload Case Notes"} subtitle={subtitle} />
      <Dialog.Body display="flex" flexDirection="column" gap={5}>
        {(listedFiles.length > 0 || row) && (
          <Stack gap={2}>
            {listedFiles.map((file) => (
              <CaseDocumentFileRow key={file.id} file={file}>
                <CaseDocumentRemoveFileButton fileName={file.name} onClick={() => removeExistingFile(file.id)} />
              </CaseDocumentFileRow>
            ))}
            {row && (
              <CaseDocumentUploadRow
                row={row}
                onReplace={(file) => replaceRow(row, file)}
                onRemove={() => removeRow(row)}
              />
            )}
          </Stack>
        )}
        {!listedFiles.length && !row && (
          <FileUploader classes="file-upload" name="file" handleChange={handleFile}>
            <CaseDocumentDropZone
              title="Drag & drop file"
              formatNames={LIMITS.formatNames}
              limits={`One file · ${formatMegaBytes(LIMITS.maxFileSize)} max`}
            />
          </FileUploader>
        )}
      </Dialog.Body>
      <Dialog.Footer gap={3} mt={2} borderTop="1px solid" borderColor="gray.200">
        <Button variant="neutral" fontSize="14px" onClick={onClose}>
          Cancel
        </Button>
        {row && (
          <Button variant="primary" fontSize="14px" disabled={!canParse} loading={isLoading} onClick={handleParse}>
            Upload & extract
          </Button>
        )}
      </Dialog.Footer>
    </>
  );
};

export default CaseNotesUploadStep;
